import { Component, Input } from '@angular/core';

import { Router, RouterLink, RouterModule } from "@angular/router";
import { Store } from '@ngrx/store';
import { authUserFeature } from '../../../store/authUser.store';
import { MatDialog } from '@angular/material/dialog';
import { ProfileMenuDialog } from './profile-menu.dialog';
import { AuthUser } from '../../../../../globalTypes/auth.types';
import { ControlsDialogComponent } from '../../admin/controls.dialog';
import { listingsFeature, ListingsPageActions } from '../../../store/listings.store';
import { Base } from '../../wrapper/base-wrapper.component';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ListingSearchComponent } from "../element/listing-search.component";
import { NotificationBellComponent } from "./notification-bell.component";

type HeaderMode = "standard" | "actionless";
@Component({
  selector: 'msh-header',
  standalone: true,
  imports: [FormsModule, RouterModule, ListingSearchComponent, NotificationBellComponent],
  template: `
    <header class="w-full px-8 z-50 fixed border-b border-gray-200 shadow-md bg-back">
      <div class="max-w-7xl mx-auto">
        <div class="flex justify-between items-center h-16">


          <div class="md:hidden w-10">&nbsp;</div>
          <!-- Logo -->
          <div class="flex-shrink-0 flex items-center">
            <a routerLink="/"><img src="96xMobileStorehouse_logo_landscape.png"
                 alt="Mobile Storehouse"
                 class="h-12 w-auto"></a>
          </div>

          <!-- Search -->
          @if(modes[mode].searchEnabled) {
          <msh-listing-search class=" md:flex hidden grow mx-8 "></msh-listing-search>
          }

          <!-- Desktop Menu -->
          @if(modes[mode].showProfileMenu) {
          <div class="hidden md:flex items-center space-x-6 text-sm font-medium">
            @if (user) {
              <!-- TODO  Better admin panel -->
              @if (user.email === "greg.a.howle+1@gmail.com"){ 
                <div (click)="openAdmin()" class="hover:text-orange-400 opacity-50 cursor-pointer font-bold transition-colors duration-300">Admin</div>
              }
              <button  (click)="navigateToPostListing()" class="reverse">
                Post a listing
              </button>
              <a [routerLink]="['listing']" routerLinkActive=" cursor-default font-bold" class="hover:text-orange-400 text-copy transition-colors duration-300">Listings</a>
              <a [routerLink]="['order']" routerLinkActive=" cursor-default font-bold" class="hover:text-orange-400 text-copy transition-colors duration-300">Orders</a>
              <msh-notification-bell></msh-notification-bell>
              <div (click)="openProfileMenu($event)" class="min-w-8 h-8 rounded-full bg-blue-400 text-white flex items-center justify-center font-bold cursor-pointer">
                {{ user.initials }}
              </div>
            } @else {

              <button routerLinkActive=" !hidden"
                [routerLinkActiveOptions]="{ exact: false }"
                [routerLink]="'/login'"
              >
                Login
              </button>
            }
          </div>
          <!-- Mobile Hamburger -->
          <div class="md:hidden flex items-center">
            <div (click)="mobileMenuOpen = !mobileMenuOpen" class="p-2 rounded cursor-pointer bg-back hover:bg-copy/50">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-6 h-6 text-copy">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </div>
          </div>
          }

        </div>
      </div>

      <!-- Mobile Dropdown -->
      @if (mobileMenuOpen) {
        <div class="md:hidden border-t border-gray-200 px-4 py-3 mb-8 space-y-3 text-copy">
          
          @if (user) {
            <button (click)="navigateToPostListing()" class="reverse">
              Post a listing
            </button>
            <a href="/listing" class="block px-3 py-2 font-bold hover:text-orange-400 transition-colors duration-300">Search</a>
            <a href="/order" class="block px-3 py-2 font-bold hover:text-orange-400 transition-colors duration-300">Orders</a>
            <div (click)="openProfileMenu($event, true)" class="flex items-center cursor-pointer space-x-2 mt-3">
              <div class="w-8 h-8 rounded-full bg-blue-400 text-white flex items-center justify-center font-bold">
                {{ user.initials }}
              </div>
              <span>{{ user.displayName }}</span>
            </div>
          } @else {
            <button  routerLinkActive=" !hidden"
                [routerLinkActiveOptions]="{ exact: false }"
                [routerLink]="'/login'">
              Login
            </button>
          }
        </div>
      }
    </header>
  `
})
export class HeaderComponent extends Base<HeaderComponent> {
  @Input() mode: HeaderMode = "standard";
  modes: {[mode in HeaderMode]: {
    searchEnabled: boolean;
    showProfileMenu: boolean;
  }} = {standard: {
    searchEnabled: true, showProfileMenu: true
  }, actionless: {
    searchEnabled: false, showProfileMenu: false
  }};
  mobileMenuOpen = false;
  isLoggedIn = false; // simulate auth
  user: AuthUser | null = {
    initials: 'GH',
    displayName: 'Greg Howle',
    avatarUrl: ''
  } as AuthUser;
  
  constructor(
    private store:Store,
    private router: Router,
    private dialog: MatDialog,
    // TODO hide header login: Load routing and hide login button when you are on the login page
    // BUG but it is a cute one, and I will keep it for now
  ) {
    super();
    this.store.select(authUserFeature.selectUser).pipe(takeUntil(this.$destroy)).subscribe(authUser => {
      this.isLoggedIn = !!authUser;
      this.user = authUser ? authUser : null;
    });
  }

  async openProfileMenu(event: MouseEvent, isMobile = false): Promise<void> {
    event.stopPropagation();
    const position = isMobile ? {} : { top: '40px', right: '20px' };
    const dialog = this.dialog.open(ProfileMenuDialog, {
      position,
      width: '350px',
      autoFocus: false,
      panelClass: 'profile-menu-panel',
      backdropClass: 'profile-menu-backdrop',
    });
    const result = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    this.mobileMenuOpen = false;
  }

  openAdmin(){
    this.dialog.open(ControlsDialogComponent, {autoFocus: false})
  }

  navigateToPostListing(){
    this.mobileMenuOpen = false;
    this.store.dispatch(ListingsPageActions.progressListing({listing: null}));
    this.router.navigate(['/manage-listing', 'photos', 'new'])
  }
}
